/**
 * Admin V3 — Financial / Ledger — Search / filter bar
 *
 * Layout (left → right):
 *   [Entry-type filter] [Driver ID input] [Search input] [Result counter]
 *
 * Entry-type filter offers the UNION of both backend enums (10 unique
 * values) — defensive both-taxonomy support so the operator can filter on
 * any historical or current ledger entry kind.
 *
 * Driver ID input: server-side via ?driver_id=
 * Search input: client-side over loaded page (reason + booking_id)
 *
 * NO CSV (no backend endpoint).
 */

import React from 'react';
import { Search, X } from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import { ledgerEntryTypeMeta } from '../../shared/visualTokens';
import { t } from '../../i18n';

const ALL_SENTINEL = '__all__';

// 10 unique values: union of LedgerEntryType (legacy 7) + OperationType
// (canonical 9), grouped by domain. Backend `entry_type` query param accepts
// any of these strings.
const ENTRY_TYPE_VALUES = [
  'payout_created', 'payout_released', 'payout_reversed',
  'adjustment_credit', 'adjustment_debit',
  'withdrawal', 'withdrawal_reserved', 'withdrawal_failed',
  'freeze', 'unfreeze',
];

const FinancialLedgerSearchBar = ({
  entryType,
  onEntryTypeChange,
  driverId,
  onDriverIdChange,
  search,
  onSearchChange,
  resultsCount,
  isFilterActive,
  loading,
  locale = 'fr',
}) => {
  const selectValue = entryType || ALL_SENTINEL;
  const handleType = (v) => onEntryTypeChange(v === ALL_SENTINEL ? '' : v);

  const resultsCountText = t('financial.ledger.results.count', locale).replace('{n}', String(resultsCount));

  return (
    <div className="flex flex-wrap items-center gap-3">
      {/* Entry-type filter */}
      <Select value={selectValue} onValueChange={handleType}>
        <SelectTrigger
          className="h-10 w-[220px] rounded-xl border-slate-200 bg-white text-sm text-slate-700"
          data-testid="adminv3-financial-ledger-type-filter"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_SENTINEL}>{t('financial.ledger.filter.typeAll', locale)}</SelectItem>
          {ENTRY_TYPE_VALUES.map((v) => (
            <SelectItem key={v} value={v}>{ledgerEntryTypeMeta[v]?.label || v}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Driver ID input (server-side) */}
      <input
        type="text"
        value={driverId}
        onChange={(e) => onDriverIdChange(e.target.value)}
        placeholder={t('financial.ledger.driverPlaceholder', locale)}
        data-testid="adminv3-financial-ledger-driver-input"
        className="h-10 w-[260px] rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm text-slate-900 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
      />

      {/* Client-side search */}
      <div className="relative flex-1 max-w-md min-w-[240px]">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
        <input
          type="text"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={t('financial.ledger.search.placeholder', locale)}
          data-testid="adminv3-financial-ledger-search"
          className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 pl-10 pr-9 text-sm text-slate-900 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
        />
        {search && (
          <button
            type="button"
            onClick={() => onSearchChange('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
            data-testid="adminv3-financial-ledger-search-clear"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {isFilterActive && !loading && (
        <span className="text-sm text-slate-400" data-testid="adminv3-financial-ledger-results-count">
          {resultsCountText}
        </span>
      )}
    </div>
  );
};

export default FinancialLedgerSearchBar;
